import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  CalendarDays,
  IndianRupee,
  RefreshCw,
  TrendingUp,
  UserRound,
  Users,
  Wallet,
} from "lucide-react";
import api from "../../helpers/api_helper";

type Period = "today" | "week" | "month" | "custom" | "all";

interface Summary {
  total_amount: number;
  deposit_count: number;
  average_deposit: number;
  unique_players: number;
}

interface DailyBreakdown {
  date: string;
  total_amount: number;
  deposit_count: number;
  unique_players: number;
}

interface WeeklyBreakdown {
  week_start: string;
  week_end: string;
  total_amount: number;
  deposit_count: number;
  unique_players: number;
}

interface MonthlyBreakdown {
  month: string;
  total_amount: number;
  deposit_count: number;
  unique_players: number;
}

interface SourceBreakdown {
  source: string;
  total_amount: number;
  deposit_count: number;
}

interface AddedByBreakdown {
  from_user: string;
  from_role: string | null;
  total_amount: number;
  deposit_count: number;
}

interface DepositTransaction {
  id: string;
  journal_id: number;
  source_type: string;
  amount: number;
  status: string;
  status_display: string;

  to_user_id: number;
  to_user: string | null;

  from_user_id: number | null;
  from_user: string | null;
  from_role: string | null;

  source: string | null;
  method: string | null;
  reference: string | null;
  description: string | null;

  balance_before: number;
  balance_after: number;

  created_at: string;
  completed_at: string;
  event_at: string;

  payment_id: number | null;
  gateway_payment_id: string | null;
}

interface AnalyticsResponse {
  success: boolean;
  period: Period;
  date_from: string | null;
  date_to: string | null;
  summary: Summary;
  breakdown: {
    daily: DailyBreakdown[];
    weekly: WeeklyBreakdown[];
    monthly: MonthlyBreakdown[];
    source: SourceBreakdown[];
    added_by: AddedByBreakdown[];
  };
  transactions: DepositTransaction[];
  pagination: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
  };
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (value?: string | null) => {
  if (!value) return "-";

  const date = new Date(`${value}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const DepositAnalytics: React.FC = () => {
  const [period, setPeriod] = useState<Period>("today");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);

  const fetchAnalytics = async (
    selectedPeriod: Period = period,
    selectedPage: number = page
  ) => {
    if (
      selectedPeriod === "custom" &&
      (!dateFrom || !dateTo)
    ) {
      setError("Please select both From and To dates.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams();
      params.set("period", selectedPeriod);
      params.set("page", String(selectedPage));
      params.set("page_size", "50");

      if (selectedPeriod === "custom") {
        params.set("date_from", dateFrom);
        params.set("date_to", dateTo);
      }

      const response = await api.get(
        `/transactions/deposit-analytics/?${params.toString()}`
      );

      setData(response.data);
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to load deposit analytics."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics(period, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, page]);

  const summary = data?.summary || {
    total_amount: 0,
    deposit_count: 0,
    average_deposit: 0,
    unique_players: 0,
  };

  const periodTitle = useMemo(() => {
    if (period === "today") return "Today";
    if (period === "week") return "Last 7 Days";
    if (period === "month") return "This Month";
    if (period === "custom") return "Custom Range";
    return "All Time";
  }, [period]);

  const handlePeriodChange = (value: Period) => {
    setPage(1);
    setPeriod(value);
  };

  const handleCustomApply = () => {
    setPage(1);
    fetchAnalytics("custom", 1);
  };

  const handleViewDay = (date: string) => {
    setDateFrom(date);
    setDateTo(date);
    setPage(1);
    setPeriod("custom");
  };

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Deposit Analytics
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Successful wallet credits with recipient, source, and added-by
            details.
          </p>
        </div>

        <button
          type="button"
          onClick={() => fetchAnalytics(period, page)}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw
            className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
          />
          Refresh
        </button>
      </div>

      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap gap-2">
          {(
            [
              ["today", "Today"],
              ["week", "7 Days"],
              ["month", "This Month"],
              ["all", "All Time"],
              ["custom", "Custom"],
            ] as Array<[Period, string]>
          ).map(([value, label]) => (
            <button
              type="button"
              key={value}
              onClick={() => handlePeriodChange(value)}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                period === value
                  ? "bg-blue-600 text-white"
                  : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {period === "custom" && (
          <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-end">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase text-gray-500">
                From
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase text-gray-500">
                To
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
              />
            </div>

            <button
              type="button"
              onClick={handleCustomApply}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Apply
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mb-6">
        <div className="mb-3 text-sm font-medium text-gray-500">
          Showing: {periodTitle}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <IndianRupee className="h-7 w-7 text-gray-700" />
              <div>
                <div className="text-xs font-medium uppercase text-gray-500">
                  Total Deposited
                </div>
                <div className="mt-1 text-2xl font-bold text-gray-900">
                  {formatCurrency(summary.total_amount)}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <Wallet className="h-7 w-7 text-gray-700" />
              <div>
                <div className="text-xs font-medium uppercase text-gray-500">
                  Deposits
                </div>
                <div className="mt-1 text-2xl font-bold text-gray-900">
                  {summary.deposit_count}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-7 w-7 text-gray-700" />
              <div>
                <div className="text-xs font-medium uppercase text-gray-500">
                  Average Deposit
                </div>
                <div className="mt-1 text-2xl font-bold text-gray-900">
                  {formatCurrency(summary.average_deposit)}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <Users className="h-7 w-7 text-gray-700" />
              <div>
                <div className="text-xs font-medium uppercase text-gray-500">
                  Unique Players
                </div>
                <div className="mt-1 text-2xl font-bold text-gray-900">
                  {summary.unique_players}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-5 py-4">
            <div className="flex items-center gap-2">
              <UserRound className="h-5 w-5 text-gray-600" />
              <h2 className="font-semibold text-gray-900">
                Added By / Source
              </h2>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-5 py-3">From</th>
                  <th className="px-5 py-3">Role</th>
                  <th className="px-5 py-3 text-right">Deposits</th>
                  <th className="px-5 py-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data?.breakdown?.added_by || []).map((row, index) => (
                  <tr key={`${row.from_user}-${index}`}>
                    <td className="px-5 py-3 font-medium text-gray-900">
                      {row.from_user || "Unknown"}
                    </td>
                    <td className="px-5 py-3 text-gray-600">
                      {row.from_role || "-"}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {row.deposit_count}
                    </td>
                    <td className="px-5 py-3 text-right font-semibold">
                      {formatCurrency(row.total_amount)}
                    </td>
                  </tr>
                ))}

                {!loading &&
                  (data?.breakdown?.added_by || []).length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-5 py-8 text-center text-gray-500"
                      >
                        No deposit data for this period.
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-5 py-4">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-gray-600" />
              <h2 className="font-semibold text-gray-900">
                Day-wise Deposits
              </h2>
            </div>
          </div>

          <div className="max-h-80 overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3 text-right">Deposits</th>
                  <th className="px-5 py-3 text-right">Players</th>
                  <th className="px-5 py-3 text-right">Amount</th>
                  <th className="px-5 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data?.breakdown?.daily || []).map((row) => (
                  <tr key={row.date}>
                    <td className="px-5 py-3 font-medium text-gray-900">
                      {formatDate(row.date)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {row.deposit_count}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {row.unique_players}
                    </td>
                    <td className="px-5 py-3 text-right font-semibold">
                      {formatCurrency(row.total_amount)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleViewDay(row.date)}
                        className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}

                {!loading &&
                  (data?.breakdown?.daily || []).length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-5 py-8 text-center text-gray-500"
                      >
                        No day-wise deposit data.
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-5 py-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-gray-600" />
              <h2 className="font-semibold text-gray-900">
                Week-wise Deposits
              </h2>
            </div>
          </div>

          <div className="max-h-72 overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-5 py-3">Week</th>
                  <th className="px-5 py-3 text-right">Deposits</th>
                  <th className="px-5 py-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data?.breakdown?.weekly || []).map((row) => (
                  <tr key={row.week_start}>
                    <td className="px-5 py-3 font-medium text-gray-900">
                      {formatDate(row.week_start)} – {formatDate(row.week_end)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {row.deposit_count}
                    </td>
                    <td className="px-5 py-3 text-right font-semibold">
                      {formatCurrency(row.total_amount)}
                    </td>
                  </tr>
                ))}

                {!loading &&
                  (data?.breakdown?.weekly || []).length === 0 && (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-5 py-8 text-center text-gray-500"
                      >
                        No week-wise deposit data.
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-5 py-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-gray-600" />
              <h2 className="font-semibold text-gray-900">
                Month-wise Deposits
              </h2>
            </div>
          </div>

          <div className="max-h-72 overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-5 py-3">Month</th>
                  <th className="px-5 py-3 text-right">Deposits</th>
                  <th className="px-5 py-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data?.breakdown?.monthly || []).map((row) => (
                  <tr key={row.month}>
                    <td className="px-5 py-3 font-medium text-gray-900">
                      {new Date(`${row.month}-01T00:00:00`).toLocaleDateString(
                        "en-IN",
                        {
                          month: "long",
                          year: "numeric",
                        }
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {row.deposit_count}
                    </td>
                    <td className="px-5 py-3 text-right font-semibold">
                      {formatCurrency(row.total_amount)}
                    </td>
                  </tr>
                ))}

                {!loading &&
                  (data?.breakdown?.monthly || []).length === 0 && (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-5 py-8 text-center text-gray-500"
                      >
                        No month-wise deposit data.
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-gray-200 px-5 py-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">
              Deposit Transactions
            </h2>
            <p className="text-xs text-gray-500">
              Exact successful wallet credits behind the totals above.
            </p>
          </div>

          <div className="text-sm text-gray-500">
            {data?.pagination?.total_items || 0} transactions
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1250px] w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Date & Time</th>
                <th className="px-4 py-3">To Player</th>
                <th className="px-4 py-3">Added By / From</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-right">Before</th>
                <th className="px-4 py-3 text-right">After</th>
                <th className="px-4 py-3">Reference</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {(data?.transactions || []).map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                    {formatDateTime(row.event_at)}
                  </td>

                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">
                      {row.to_user || `User #${row.to_user_id}`}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">
                      {row.from_user || "Unknown"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {row.from_role || "-"}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="text-gray-900">
                      {row.source || "-"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {row.method || "-"}
                    </div>
                  </td>

                  <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-gray-900">
                    {formatCurrency(row.amount)}
                  </td>

                  <td className="whitespace-nowrap px-4 py-3 text-right text-gray-600">
                    {formatCurrency(row.balance_before)}
                  </td>

                  <td className="whitespace-nowrap px-4 py-3 text-right font-medium text-gray-900">
                    {formatCurrency(row.balance_after)}
                  </td>

                  <td className="max-w-[220px] truncate px-4 py-3 text-gray-600">
                    {row.gateway_payment_id ||
                      row.reference ||
                      `Journal #${row.journal_id}`}
                  </td>

                  <td className="px-4 py-3">
                    <span className="inline-flex rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700">
                      {row.status_display || "Completed"}
                    </span>
                  </td>
                </tr>
              ))}

              {loading && (
                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-10 text-center text-gray-500"
                  >
                    Loading deposit analytics...
                  </td>
                </tr>
              )}

              {!loading && (data?.transactions || []).length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-10 text-center text-gray-500"
                  >
                    No successful deposits found for this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {(data?.pagination?.total_pages || 0) > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-5 py-4">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 disabled:opacity-40"
            >
              Previous
            </button>

            <div className="text-sm text-gray-500">
              Page {data?.pagination?.page || page} of{" "}
              {data?.pagination?.total_pages || 1}
            </div>

            <button
              type="button"
              disabled={
                page >= (data?.pagination?.total_pages || 1) || loading
              }
              onClick={() => setPage((current) => current + 1)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DepositAnalytics;
